import { useMutation } from '@tanstack/react-query'; 
import { useAuthContext } from './useAuthContext';
import { useStateContext } from '../context/StateContext';
import axios from 'axios';
import { useErrorBoundary } from 'react-error-boundary';
import { optimizeImage } from './imageOptimizer';

/**
 * A wrapper around useMutation that always makes a network request and never retries.
 * Useful for instant mutations where the response is not cached.
 * #queries #mutations
 * @param {function} mutationFn The function to call when the mutation is triggered.
 * @param {object} options Options to pass to useMutation.
 * @returns {ReactQueryMutation} A ReactQueryMutation object.
 */
export const useInstantlMutation = (mutationFn, options = {}) => {
  return useMutation(mutationFn, {
    networkMode: 'always',
    retry: false,
    ...options,
  });
};

/**
 * #queries #mutations
 * Makes a mutation call to the specified route with the given data.
 * @param {object} data The data to pass to the mutation call.
 * @param {string} route The route of the mutation call.
 * @param {object} user The user to use for authentication.
 * @param {function} onSuccess The function to call when the mutation call is successful.
 * @param {function} onError The function to call when the mutation call fails.
 * @param {number} retryCount The number of times to retry the mutation call.
 * @param {number} maxRetries The maximum number of times to retry the mutation call.
 * @returns {Promise<object>} A promise that resolves with the response data.
 * @throws {Error} If the mutation call fails.
 */
export const mutationCall = async (data,{route, user, onSuccess=()=>{}, onError=()=>{}, retryCount=0, maxRetries=2}) => {
    try {
        if(!route) throw Error('no route')
        if(!user) throw Error('no user')
        if(!user.token) throw Error('no token')
        if(!data) throw Error('no data')
        if(data?.doc?._id == "7ab3fe86-165b-4dc7-977c-f5f2d2a8a7d8"){
            throw new Error('wlkedlwk Custom error in mutations.js for POLVERE ABC POS 2 EST DEMO' );
        }

        console.log(`${route} mutation call (attempt ${retryCount + 1}/${maxRetries + 1})`, data)
        
        // Test error after validation but before axios call
        //throw new Error('test error')

        const response = await axios.post(route, {
            data 
        }, {
            withCredentials: true, 
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${user.token}`,
                'X-Retry-Count': retryCount.toString() // Add retry count to headers
            },
            timeout: 50000, // 50 second timeout (leaving buffer for Vercel's 60s limit)
            timeoutErrorMessage: 'Request took too long to complete. Please try again.'
        });
        
        const responseData = response.data   
        if(responseData){
            // Clear any stored retry information for this route
            if (typeof window !== 'undefined' && window.sessionStorage) {
                try {
                    if (sessionStorage.getItem('lastTimeoutRoute') === route) {
                        sessionStorage.removeItem('lastTimeoutRoute');
                        sessionStorage.removeItem('lastTimeoutTime');
                        sessionStorage.removeItem('timeoutRetryCount');
                    }
                } catch (storageError) {
                    console.error('Error clearing timeout information:', storageError);
                }
            }
            
            onSuccess(responseData)
            return responseData
        }
    } catch(e) {
        console.log(`${route} catch error`, e)
        
        // Simple error handling - just get the error message
        let errorMessage = e?.message;
        
        // If backend sent an error response, use that message
        if (e?.response?.data?.error) {
            errorMessage = e.response.data.error;
        }
        
        // Create error object with the message
        const errorToThrow = new Error(errorMessage);
        
        // Call the onError callback
        onError(errorToThrow)
        
        // Propagate error to component's onError handler
        throw errorToThrow
    }
} 

/**
 * Makes a POST request to the given route with the given data and user object.
 * This function handles file uploads specifically and optimizes images before upload.
 * It also includes retry logic for network and timeout errors.
 * #queries #mutations
 * @param {Object} data - object with file data to upload, or file and data if no file is present
 * @param {Object} params - object with route, user, onSuccess, onError, onProgress, retryCount, and maxRetries properties
 * @param {string} params.route - the route to make the request to
 * @param {Object} params.user - object with a token property
 * @param {Function} params.onSuccess - callback to call with response data on success
 * @param {Function} params.onError - callback to call with error message on failure
 * @param {Function} params.onProgress - callback to call with progress data on upload
 * @param {number} params.retryCount - number of times to retry request on failure
 * @param {number} params.maxRetries - maximum number of times to retry request on failure
 * @returns {Promise<Object>} - promise that resolves with the response data or rejects with an error
 */
export const fileMutationCall = async (data, {route, user, onSuccess=()=>{}, onError=()=>{}, onProgress=()=>{}, retryCount=0, maxRetries=2}) => {
    try {
        if(!route) throw Error('no route')
        if(!user) throw Error('no user')
        if(!user.token) throw Error('no token')

        const formData = new FormData()
        
        // Handle file data specifically
        if (data.file) {
            let fileToUpload = data.file;
            
            // If it's an image, optimize it before upload
            if (data.file.type.startsWith('image/')) {
                fileToUpload = await optimizeImage(data.file);
            }
            
            formData.append('file', fileToUpload)
            // Remove file from data object before appending rest of data
            const { file, ...restData } = data
            formData.append('data', JSON.stringify(restData))
        } else {
            formData.append('data', JSON.stringify(data))
        }

        const response = await axios.post(route, formData, {
            withCredentials: true,
            headers: {
                'Authorization': `Bearer ${user.token}`,
                'X-Retry-Count': retryCount.toString(), // Add retry count to headers
                // Don't set Content-Type here, let axios set it with boundary for FormData
            },
            timeout: 30000, // 30 second timeout should be plenty after optimization
            timeoutErrorMessage: 'File upload took too long to complete. Please try again.',
            onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                onProgress(percentCompleted);
            }
        });

        const responseData = response.data
        if(responseData){
            // Clear any stored retry information for this route
            if (typeof window !== 'undefined' && window.sessionStorage) {
                try {
                    if (sessionStorage.getItem('lastFileTimeoutRoute') === route) {
                        sessionStorage.removeItem('lastFileTimeoutRoute');
                        sessionStorage.removeItem('lastFileTimeoutTime');
                        sessionStorage.removeItem('fileTimeoutRetryCount');
                    }
                } catch (storageError) {
                    console.error('Error clearing file timeout information:', storageError);
                }
            }
            
            onSuccess(responseData)
            return responseData
        }
    } catch(e) {
        console.log(`${route} catch error (attempt ${retryCount + 1}/${maxRetries + 1})`, e)
        
        // Enhanced error handling with better categorization
        let errorMessage = e?.message;
        let errorType = 'unknown';
        
        // Network and timeout errors
        let shouldRetry = false;
        
        if (e?.code === 'ECONNABORTED') {
            errorMessage = 'File upload timed out. Please try again.';
            errorType = 'timeout';
            shouldRetry = true;
        } else if (e?.response?.status === 504) {
            errorMessage = 'Gateway Timeout: The server took too long to respond. Your file might still be processing. Please wait a moment and check if your upload was completed before trying again.';
            errorType = 'timeout';
            shouldRetry = true;
            
            // Log detailed information about the Gateway Timeout
            console.warn('Gateway Timeout (504) detected during file upload. This may be a temporary issue with the server or network.');
            
            // Store information about the failed request in sessionStorage
            if (typeof window !== 'undefined') {
                try {
                    const timeoutInfo = {
                        route,
                        timestamp: new Date().toISOString(),
                        retryCount: (parseInt(sessionStorage.getItem('fileTimeoutRetryCount') || '0') + 1)
                    };
                    sessionStorage.setItem('lastFileTimeoutRoute', route);
                    sessionStorage.setItem('lastFileTimeoutTime', timeoutInfo.timestamp);
                    sessionStorage.setItem('fileTimeoutRetryCount', timeoutInfo.retryCount.toString());
                    console.log('File timeout information stored:', timeoutInfo);
                } catch (storageError) {
                    console.error('Error storing file timeout information:', storageError);
                }
            }
        } else if (e?.message === 'Network Error') {
            errorMessage = 'Network connection error. Please check your internet connection and try again.';
            errorType = 'network';
            shouldRetry = true;
        }
        
        // Authentication errors
        else if (e?.response?.status === 401) {
            errorMessage = 'Authentication failed. Please log in again.';
            errorType = 'auth';
            shouldRetry = false; // Don't retry auth errors
            
            // Clear token if it's invalid - this will trigger a re-login
            if (typeof window !== 'undefined') {
                localStorage.removeItem('token');
            }
        }
        
        // File-specific errors
        else if (e?.response?.status === 413) {
            errorMessage = 'File is too large. Please try a smaller file.';
            errorType = 'file_size';
            shouldRetry = false; // Don't retry file size errors
        }
        
        // CORS errors - these are harder to detect directly
        else if (!e?.response && e?.message === 'Network Error') {
            // This is often a CORS error
            errorMessage = 'Cross-origin request failed. This might be a temporary issue. Try clearing your browser cache or reloading the page.';
            errorType = 'cors';
            shouldRetry = true;
            
            console.error('Possible CORS error:', e);
            
            // Suggest clearing localforage if this is a repeated CORS error
            if (typeof window !== 'undefined' && window.localforage) {
                console.log('Consider clearing localforage data if this error persists.');
            }
        }
        
        // Implement client-side retry logic
        if (shouldRetry && retryCount < maxRetries) {
            console.log(`Retrying file upload (${retryCount + 1}/${maxRetries})...`);
            
            // Add exponential backoff
            const backoffTime = Math.min(1000 * Math.pow(2, retryCount), 5000);
            console.log(`Waiting ${backoffTime}ms before retry...`);
            
            // Wait for backoff time
            await new Promise(resolve => setTimeout(resolve, backoffTime));
            
            // Retry the request with incremented retry count
            return fileMutationCall(data, {
                route, 
                user, 
                onSuccess, 
                onError, 
                onProgress,
                retryCount: retryCount + 1, 
                maxRetries
            });
        }
        
        // If we've reached max retries or shouldn't retry, show error to user
        if (retryCount >= maxRetries) {
            console.log(`All ${maxRetries + 1} file upload attempts failed. Showing error to user.`);
            
            // Add information about retry attempts to error message
            errorMessage = `${errorMessage} (After ${maxRetries + 1} attempts)`;
        }
        
        // Only show error to user if we've reached max retries or shouldn't retry
        onError({ ...e, message: errorMessage, errorType, retryCount, maxRetries })
        throw e // Propagate error to component's onError handler
    }
}

//===============================

/**
 * Hook to perform a create-replace mutation.
 * #queries #mutations
 * It wraps useInstantlMutation from react-query and adds error handling using useErrorBoundary.
 * 
 * @param {Object} props - The props to pass to useInstantlMutation
 * @returns {Object} - The result of useInstantlMutation
 */
export const useCreateReplace = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useInstantlMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/create-replace`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
} 



/**
 * Hook to perform a file upload mutation.
 * It wraps useInstantlMutation from react-query and adds error handling using useErrorBoundary.
 * #queries #mutations
 * @param {Object} props - The props to pass to useInstantlMutation
 * @returns {Object} - The result of useInstantlMutation
 * 
 * The onProgress callback is called with an object containing the progress of the file upload.
 * The object has the following shape: { loaded: number, total: number }
 * 
 * Example usage:
 * const {mutate: uploadPhoto} = useUploadPhoto({
 *     onSuccess: (data) => console.log(data),
 *     onError: (error) => console.error(error),
 *     onProgress: (progress) => console.log(progress.loaded, progress.total)
 * })
 */
export const useUploadPhoto = ({onProgress, ...props}) => {
    //const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useInstantlMutation(
        (data) => fileMutationCall(data, {
            route: `${apiUrl}/calls/add-file`,
            user,
            onError: (e) => {throw new Error(e.message)},
            onProgress: onProgress || (() => {})
        }),
        {...props}
    )
}

/**
 * Hook to perform a file upload mutation.
 * It wraps useInstantlMutation from react-query and adds error handling using useErrorBoundary.
 * #queries #mutations
 * @param {Object} props - The props to pass to useInstantlMutation
 * @returns {Object} - The result of useInstantlMutation
 * 
 * The onProgress callback is called with an object containing the progress of the file upload.
 * The object has the following shape: { loaded: number, total: number }
 * 
 * Example usage:
 * const {mutate: uploadFile} = useUploadFile({
 *     onSuccess: (data) => console.log(data),
 *     onError: (error) => console.error(error),
 *     onProgress: (progress) => console.log(progress.loaded, progress.total)
 * })
 */
export const useUploadFile = ({onProgress, ...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useInstantlMutation(
        (data) => fileMutationCall(data, {
            route: `${apiUrl}/calls/add-file`,
            user,
            onError: (e) => showBoundary(new Error(e.message)),
            onProgress: onProgress || (() => {})
        }),
        {...props}
    )
}

/**
 * Hook to perform a delete-file mutation.
 * It wraps useInstantlMutation from react-query and adds error handling using useErrorBoundary.
 * #queries #mutations
 * @param {Object} props - The props to pass to useInstantlMutation
 * @returns {Object} - The result of useInstantlMutation
 */
export const useDeletePhoto = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useInstantlMutation((data)=>fileMutationCall(data, {route: `${apiUrl}/calls/delete-file`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
}

/**
 * Hook to perform a delete-document mutation.
 * It wraps useInstantlMutation from react-query and adds error handling using useErrorBoundary.
 * #queries #mutations
 * @param {Object} props - The props to pass to useInstantlMutation
 * @returns {Object} - The result of useInstantlMutation
 */
export const useDeleteDocument = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useInstantlMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/delete`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
}

/**
 * Hook to perform a prepend mutation.
 * It wraps useInstantlMutation from react-query and adds error handling using useErrorBoundary.
 * #queries #mutations
 * @param {Object} props - The props to pass to useInstantlMutation
 * @returns {Object} - The result of useInstantlMutation
 */
export const usePrepend = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useInstantlMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/prepend`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
}

/**
 * Hook to perform a set-at-index mutation.
 * It wraps useInstantlMutation from react-query and adds error handling using useErrorBoundary.
 * #queries #mutations
 * @param {Object} props - The props to pass to useInstantlMutation
 * @returns {Object} - The result of useInstantlMutation
 */
export const useSet = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useInstantlMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/set-at-index`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
}
/**
 * Hook to perform a set mutation.
 * It wraps useInstantlMutation from react-query and adds error handling using useErrorBoundary.
 * #queries #mutations
 * @param {Object} props - The props to pass to useInstantlMutation
 * @returns {Object} - The result of useInstantlMutation
 * 
 * This hook only passes the data argument to the mutation call.
 * If the mutation needs additional data, it should be passed as part of the data argument.
 * Example usage:
 * const {mutate: setData} = useSetOnlyPassedData({
 *     onSuccess: (data) => console.log(data),
 *     onError: (error) => console.error(error),
 *     data: {
 *         // data to pass to the mutation
 *     }
 * })
 */
export const useSetOnlyPassedData = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useInstantlMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/set`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
}

/**
 * Hook to perform an unset mutation.
 * It wraps useInstantlMutation from react-query and adds error handling using useErrorBoundary.
 * #queries #mutations
 * @param {Object} props - The props to pass to useInstantlMutation
 * @returns {Object} - The result of useInstantlMutation
 * 
 * This hook only passes the data argument to the mutation call.
 * If the mutation needs additional data, it should be passed as part of the data argument.
 * Example usage:
 * const {mutate: unset} = useUnset({
 *     onSuccess: (data) => console.log(data),
 *     onError: (error) => console.error(error),
 *     data: {
 *         // data to pass to the mutation
 *     }
 * })
 */
export const useUnset = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useInstantlMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/unset`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
}

/**
 * Hook to perform a replace-in-array mutation.
 * It wraps useInstantlMutation from react-query and adds error handling using useErrorBoundary.
 * #queries #mutations
 * @param {Object} props - The props to pass to useInstantlMutation
 * @returns {Object} - The result of useInstantlMutation
 * 
 * This hook only passes the data argument to the mutation call.
 * If the mutation needs additional data, it should be passed as part of the data argument.
 * Example usage:
 * const {mutate: replaceInArray} = useReplaceInArray({
 *     onSuccess: (data) => console.log(data),
 *     onError: (error) => console.error(error),
 *     data: {
 *         // data to pass to the mutation
 *     }
 * })
 */
export const useReplaceInArray = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useInstantlMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/replace-in-array`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
}

/**
 * Hook to perform a delete-in-array mutation.
 * It wraps useInstantlMutation from react-query and adds error handling using useErrorBoundary.
 * #queries #mutations
 * @param {Object} props - The props to pass to useInstantlMutation
 * @returns {Object} - The result of useInstantlMutation
 * 
 * This hook only passes the data argument to the mutation call.
 * If the mutation needs additional data, it should be passed as part of the data argument.
 * Example usage:
 * const {mutate: deleteInArray} = useDeleteInArray({
 *     onSuccess: (data) => console.log(data),
 *     onError: (error) => console.error(error),
 *     data: {
 *         // data to pass to the mutation
 *     }
 * })
 */
export const useDeleteInArray = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useInstantlMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/delete-in-array`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
}



/**
 * Hook to perform a replace-device mutation.
 * It wraps useInstantlMutation from react-query and adds error handling using useErrorBoundary.
 * #queries #mutations
 * @param {Object} props - The props to pass to useInstantlMutation
 * @returns {Object} - The result of useInstantlMutation
 * 
 * This hook only passes the data argument to the mutation call.
 * If the mutation needs additional data, it should be passed as part of the data argument.
 * Example usage:
 * const {mutate: replaceDevice} = useReplaceDevice({
 *     onSuccess: (data) => console.log(data),
 *     onError: (error) => console.error(error),
 *     data: {
 *         // data to pass to the mutation
 *     }
 * })
 */
export const useReplaceDevice = ({...props}) => {
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useInstantlMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/replace-device`, user}),{...props})
}

/**
 * Hook to perform an append mutation.
 * It wraps useInstantlMutation from react-query and adds error handling by rethrowing the error.
 * #queries #mutations
 * @param {Object} props - The props to pass to useInstantlMutation
 * @returns {Object} - The result of useInstantlMutation
 * 
 * This hook only passes the data argument to the mutation call.
 * If the mutation needs additional data, it should be passed as part of the data argument.
 * Example usage:
 * const {mutate: append} = useAppend({
 *     onSuccess: (data) => console.log(data),
 *     onError: (error) => console.error(error),
 *     data: {
 *         // data to pass to the mutation
 *     }
 * })
 */
export const useAppend = ({...props}) => {
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useInstantlMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/append`, user, onError:(e)=>{throw new Error(e.message)}  }),{...props})
}

/**
 * Hook to perform a batch append mutation for arrays.
 * It wraps useInstantlMutation from react-query and adds error handling by rethrowing the error.
 * #queries #mutations
 * @param {Object} props - The props to pass to useInstantlMutation
 * @returns {Object} - The result of useInstantlMutation
 * 
 * This hook is designed for batch appending arrays of documents.
 * The data.doc should be an array of documents to append.
 * Example usage:
 * const {mutate: appendArray} = useAppendArray({
 *     onSuccess: (data) => console.log(data),
 *     onError: (error) => console.error(error),
 * })
 */
export const useAppendArray = ({...props}) => {
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useInstantlMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/append-array`, user, onError:(e)=>{throw new Error(e.message)}  }),{...props})
}

/**
 * Hook to perform a replace-offer mutation.
 * It wraps useInstantlMutation from react-query and adds error handling using useErrorBoundary.
 * #queries #mutations
 * @param {Object} props - The props to pass to useInstantlMutation
 * @returns {Object} - The result of useInstantlMutation
 * 
 * This hook only passes the data argument to the mutation call.
 * If the mutation needs additional data, it should be passed as part of the data argument.
 * Example usage:
 * const {mutate: replaceOffer} = useReplaceOffer({
 *     onSuccess: (data) => console.log(data),
 *     onError: (error) => console.error(error),
 *     data: {
 *         // data to pass to the mutation
 *     }
 * })
 */
export const useReplaceOffer = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useInstantlMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/replace-offer`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
}

/**
 * Hook to perform a replace-price-list mutation.
 * It wraps useInstantlMutation from react-query and adds error handling using useErrorBoundary.
 * #queries #mutations
 * 
 * @param {Object} props - The props to pass to useInstantlMutation
 * @returns {Object} - The result of useInstantlMutation
 * 
 * This hook only passes the data argument to the mutation call.
 * If the mutation needs additional data, it should be passed as part of the data argument.
 * Example usage:
 * const {mutate: replacePriceList} = useReplacePriceList({
 *     onSuccess: (data) => console.log(data),
 *     onError: (error) => console.error(error),
 *     data: {
 *         // data to pass to the mutation
 *     }
 * })
 */
export const useReplacePriceList = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useInstantlMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/replace-price-list`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
}
